import { useAuth } from '@clerk/react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useState } from 'react';
import {
  AGENT_CARD_BUTTON_DARK,
  AgentCard,
} from '@/components/agent-chat/agent-card.component';
import {
  connectionHref,
  connectionsOf,
  isWorkspaceKind,
  SEQUENCER_CONNECTION_KINDS,
  type ConnectionNeed,
} from '@/components/agent-chat/agent-cards.util';
import {
  CardFooter,
  CardMeta,
  CardRow,
  CardRows,
  CardStatus,
  CardText,
} from '@/components/agent-chat/card-parts.component';
import { PlanRequiredCard } from '@/components/agent-chat/plan-required-card.component';
import { useInlineConnect } from '@/components/agent-chat/use-inline-connect.hook';
import { AppIcon, type IconDefinition } from '@/components/ui/app-icon';
import { serverReason } from '@/containers/agents/server-reason.util';
import { GongConnectDialog } from '@/containers/integrations/gong-connect-dialog.component';
import { LemlistConnectDialog } from '@/containers/integrations/lemlist-connect-dialog.component';
import {
  SequencerConnectButton,
  SequencerLogo,
  type EngagementProvider,
} from '@/containers/integrations/sequencer-connect.component';
import { WORKSPACE_PROVIDERS } from '@/containers/integrations/workspace-connection-card.component';
import { ownMailboxes } from '@/containers/playbooks/custom-builder/config/mailbox-email.util';
import type { OutsideProvider } from '@/containers/playbooks/custom-builder/sequence-step.util';
import { ConnectMailboxDialog } from '@/containers/sequences/sender-connections/connect-mailbox-dialog.component';
import { LinkedInConnectDialog } from '@/containers/sequences/sender-connections/linkedin-connect-dialog.component';
import { useLinkedInLocked } from '@/containers/sequences/use-sequencing-access.hook';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useInstantlyEnabled } from '@/hooks/use-instantly-enabled.hook';
import { useTeamsEnabled } from '@/hooks/use-teams-enabled.hook';
import {
  iconAttio,
  iconEnvelope,
  iconHubspot,
  iconLinkedin,
  iconSalesforce,
  iconSlack,
  iconSpinnerThird,
  iconTeams,
} from '@/lib/icons';
import { useSetPlayMailboxMutation } from '@/store/features/gtm-agents.feature';

/** The apps whose logo is one of the app's icons. */
const CONNECTION_ICONS: Record<string, IconDefinition> = {
  salesforce: iconSalesforce,
  hubspot: iconHubspot,
  attio: iconAttio,
  slack: iconSlack,
  teams: iconTeams,
  linkedin: iconLinkedin,
};

function AppLogo({ kind }: { kind: string }) {
  if (isWorkspaceKind(kind)) {
    const { Icon } = WORKSPACE_PROVIDERS[kind];
    return <Icon className='size-[12px]' />;
  }
  if (SEQUENCER_CONNECTION_KINDS.includes(kind)) {
    return (
      <SequencerLogo
        provider={kind as OutsideProvider}
        className='size-[12px]'
      />
    );
  }
  return (
    <AppIcon
      icon={CONNECTION_ICONS[kind] ?? iconEnvelope}
      aria-hidden='true'
      style={{ fontSize: 12 }}
    />
  );
}

function LogoTile({ kind }: { kind: string }) {
  return (
    <div className='flex size-[20px] flex-none items-center justify-center rounded-[6px] border border-border bg-background'>
      <AppLogo kind={kind} />
    </div>
  );
}

/** The card's one dark button; a spinner while the sign-in starts. */
function ConnectCardButton({
  label,
  onClick,
  busy = false,
  disabled = false,
}: {
  label: string;
  onClick: () => void;
  busy?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={busy || disabled}
      aria-busy={busy || undefined}
      className={AGENT_CARD_BUTTON_DARK}
    >
      {busy ? (
        <AppIcon icon={iconSpinnerThird} spin style={{ fontSize: 12 }} />
      ) : null}
      {label}
    </button>
  );
}

type InlineConnect = ReturnType<typeof useInlineConnect>;

/**
 * The card's button for each app's flow (see `inlineConnectFlow`): every app
 * connects in place, Teams and an app the card doesn't know open
 * Integrations in a new tab. The dialog flows open `ConnectDialog`.
 */
function ConnectAction({
  kind,
  label,
  connect,
  onOpenDialog,
}: {
  kind: string;
  label: string;
  connect: InlineConnect;
  onOpenDialog: () => void;
}) {
  const { flow, orgId, phase, start, arm, refetch } = connect;
  const connected = () => {
    arm();
    refetch();
  };
  if (flow === 'link')
    return (
      <a
        href={connectionHref(kind)}
        target='_blank'
        rel='noopener noreferrer'
        onClick={arm}
        className={AGENT_CARD_BUTTON_DARK}
      >
        {label}
      </a>
    );
  if (flow === 'sequencer')
    return (
      <SequencerConnectButton
        orgId={orgId}
        provider={kind as EngagementProvider}
        onConnected={connected}
        trigger={(onClick, busy) => (
          <ConnectCardButton
            label={label}
            onClick={onClick}
            busy={busy}
            disabled={!orgId}
          />
        )}
      />
    );
  return (
    <ConnectCardButton
      label={label}
      onClick={flow === 'redirect' ? start : onOpenDialog}
      busy={phase === 'starting'}
      disabled={!orgId || !connect.linkedinReady}
    />
  );
}

/**
 * The dialog a flow connects in, kept beside the card rather than in it: the
 * mailbox dialog goes on to its checks after the card already reads
 * connected.
 */
function ConnectDialog({
  connect,
  open,
  onOpenChange,
}: {
  connect: InlineConnect;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { flow, orgId, phase, start, arm, refetch } = connect;
  const connected = () => {
    arm();
    refetch();
  };
  if (flow === 'mailbox')
    return (
      <ConnectMailboxDialog
        open={open}
        onOpenChange={onOpenChange}
        onDone={connected}
      />
    );
  if (flow === 'linkedin')
    return (
      <LinkedInConnectDialog
        open={open}
        account={connect.linkedinReconnect}
        onOpenChange={onOpenChange}
        onConnected={connected}
      />
    );
  if (flow === 'lemlist')
    return (
      <LemlistConnectDialog
        open={open}
        onOpenChange={onOpenChange}
        onConnected={connected}
      />
    );
  if (flow === 'gong')
    return (
      <GongConnectDialog
        organizationId={orgId}
        open={open}
        onOpenChange={onOpenChange}
        onConnectOAuth={() => {
          // The sign-in opens in its window; the card waits for it.
          start();
          onOpenChange(false);
        }}
        connectingOAuth={phase === 'starting'}
      />
    );
  return null;
}

/**
 * An app the agent needs, by its logo and name, connected from the card
 * itself. Once the app reads back connected, the header says so and nothing
 * else shows.
 */
function ConnectionCard({
  need,
  afterConnect,
}: {
  need: ConnectionNeed;
  /** A run stopped on it: Connected only once it connects here. */
  afterConnect: boolean;
}) {
  const { kind, name } = need;
  const connect = useInlineConnect(kind, name, {
    initiallyConnected: need.connected,
    afterConnect,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  if (connect.hidden) return null;
  // Sentence case: "Connect email", "Connect Salesforce".
  const label = `Connect ${kind === 'email' ? 'email' : name}`;
  return (
    <>
      <AgentCard
        data-connection-card
        leading={<LogoTile kind={kind} />}
        title={name}
        trailing={
          connect.connected ? (
            <CardStatus tone='success'>Connected</CardStatus>
          ) : connect.phase === 'waiting' ? (
            <CardStatus>Connecting</CardStatus>
          ) : null
        }
      >
        {connect.connected ? null : (
          <>
            <CardText>{label}</CardText>
            {connect.error ? (
              <CardMeta tone='danger'>{connect.error}</CardMeta>
            ) : null}
            <div className='flex justify-end'>
              <ConnectAction
                kind={kind}
                label={label}
                connect={connect}
                onOpenDialog={() => setDialogOpen(true)}
              />
            </div>
          </>
        )}
      </AgentCard>
      <ConnectDialog
        connect={connect}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </>
  );
}

/**
 * The play's Send an email steps (`nodeIds`) need a mailbox of the member's
 * own: their connected mailboxes as rows (the one picked when there's only
 * one), saved onto those steps. With none yet, it connects one first. Once
 * saved, the header shows the mailbox.
 */
function MailboxPickCard({
  need,
  agentId,
  playbookId,
}: {
  need: ConnectionNeed;
  agentId: string;
  playbookId: string;
}) {
  const { userId } = useAuth();
  const connect = useInlineConnect('email', need.name, {
    initiallyConnected: false,
  });
  const loading = !connect.mailboxesLoaded;
  const [dialogOpen, setDialogOpen] = useState(false);
  const mine = ownMailboxes(connect.mailboxes, userId, undefined);
  const [chosen, setChosen] = useState<string | null>(null);
  const selected =
    mine.find((mailbox) => mailbox.id === chosen)?.id ??
    (mine.length === 1 ? mine[0].id : null);
  const [save] = useSetPlayMailboxMutation();
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSave = async () => {
    if (!selected) return false;
    setError(null);
    try {
      const result = await save({
        agentId,
        playbookId,
        nodeIds: need.nodeIds,
        mailboxId: selected,
      }).unwrap();
      setSaved(
        result.mailbox_email ||
          mine.find((mailbox) => mailbox.id === selected)?.email_address ||
          'Saved'
      );
      return true;
    } catch (refused) {
      setError(serverReason(refused, 'Couldn’t save the mailbox. Try again.'));
      return false;
    }
  };

  return (
    <>
      <AgentCard
        data-connection-card
        leading={<LogoTile kind='email' />}
        title={need.name}
        trailing={
          saved ? (
            <CardStatus tone='success'>{saved}</CardStatus>
          ) : loading ? (
            <CardStatus>Loading</CardStatus>
          ) : null
        }
      >
        {saved || loading ? null : mine.length === 0 ? (
          <>
            <CardText>Connect email</CardText>
            <div className='flex justify-end'>
              <ConnectCardButton
                label='Connect email'
                onClick={() => setDialogOpen(true)}
                disabled={!connect.orgId}
              />
            </div>
          </>
        ) : (
          <>
            <CardRows label='Mailbox' selectable>
              {mine.map((mailbox) => (
                <CardRow
                  key={mailbox.id}
                  label={mailbox.email_address}
                  selected={mailbox.id === selected}
                  onSelect={() => setChosen(mailbox.id)}
                />
              ))}
            </CardRows>
            {error ? <CardMeta tone='danger'>{error}</CardMeta> : null}
            <CardFooter
              primary={{
                label: 'Use mailbox',
                workingLabel: 'Saving',
                onClick: onSave,
                disabled: !selected,
              }}
            />
          </>
        )}
      </AgentCard>
      <ConnectDialog
        connect={connect}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </>
  );
}

/**
 * The connection cards for a `connection_required` result: one per app the
 * result names (a failed Try or dry run can name several), each read live so
 * it turns to connected once the org connects it.
 */
export function ConnectionRequiredCard({
  display,
  result,
}: {
  display: unknown;
  result: unknown;
}) {
  const instantly = useInstantlyEnabled();
  const teams = useTeamsEnabled();
  const attio = useFeatureFlagEnabled('attio_crm_v1') === true;
  const linkedinLocked = useLinkedInLocked();
  // Until the plan read settles (or if it failed), offer no LinkedIn sign-in.
  const planSettled = useFeatureAccess().isReady === true;
  const connections = connectionsOf(display, result);
  if (!connections) return null;
  const { agentId, playbookId } = connections;
  // Instantly's connect card shows only with instantly_v1 on, Teams' only
  // with teams_notify_v1 on, Attio's only with attio_crm_v1 on.
  const needs = connections.needs.filter(
    (need) =>
      !(need.kind === 'instantly' && !instantly) &&
      !(need.kind === 'teams' && !teams) &&
      !(need.kind === 'attio' && !attio)
  );
  const cards = needs.map((need, index) =>
    // LinkedIn connects on Pro and up: below it, the way in instead.
    need.kind === 'linkedin' && !planSettled ? null : need.kind ===
        'linkedin' && linkedinLocked ? (
      <PlanRequiredCard
        key={`${need.kind}:${index}`}
        result={{
          required_plan: 'Pro',
          message: 'LinkedIn connects on Pro and up.',
        }}
      />
    ) : need.kind === 'email' &&
      need.nodeIds.length > 0 &&
      agentId &&
      playbookId ? (
      <MailboxPickCard
        key={`${need.kind}:${index}`}
        need={need}
        agentId={agentId}
        playbookId={playbookId}
      />
    ) : (
      <ConnectionCard
        key={`${need.kind}:${index}`}
        need={need}
        afterConnect={connections.listed && need.reason === 'connect'}
      />
    )
  );
  if (cards.length === 0) return null;
  if (cards.length === 1) return cards[0];
  return <div className='flex flex-col gap-[8px]'>{cards}</div>;
}
